<!--
  线性零部件阵列属性面板组件。

  文件定位：
  - 对应 SOLIDWORKS“线性零部件阵列 PropertyManager”的 UI 层；
  - 当前阶段只实现界面结构与本地输入状态，不执行几何求解；
  - 后续 cad-core 控制器接入后，本组件只负责把用户输入映射到控制器状态。
-->
<template>
  <div class="linear-component-pattern-property" data-testid="linear-component-pattern-property-section">
    <n-collapse
      :expanded-names="expandedNames"
      class="pm-collapse"
      @update:expanded-names="handleExpandedNamesChange"
    >
      <n-collapse-item title="方向 1(1)" name="direction1">
        <div class="group-block">
          <div class="selection-row">
            <span class="field-label">阵列方向</span>
            <LinearComponentPatternReferenceList
              :items="direction1.directionItems"
              :active="activeReferenceInputField === LinearComponentPatternReferenceInputField.Direction1"
              empty-text="未选中任何方向"
              :visible-rows="1"
              :show-delete-all-action="false"
              @activate="activateReferenceInput(LinearComponentPatternReferenceInputField.Direction1)"
              @delete-item="removeReferenceItem(LinearComponentPatternReferenceInputField.Direction1, $event.index)"
              @delete-all="clearReferenceInput(LinearComponentPatternReferenceInputField.Direction1)"
            />
          </div>
          <!-- 线性零部件阵列-阵列方向反向 开始 -->
          <div class="button-row">
            <span class="field-label"></span>
            <n-checkbox
              :checked="direction1.reverseDirection === true"
              @update:checked="handleDirection1ReverseDirectionChange"
            >
              反转方向
            </n-checkbox>
          </div>
          <!-- 线性零部件阵列-阵列方向反向 结束 -->
          <n-radio-group
            :value="direction1.mode"
            size="small"
            @update:value="handleDirectionModeChange(
              direction1,
              $event,
              LinearComponentPatternReferenceInputField.Direction1,
              LinearComponentPatternReferenceInputField.Direction1ReferenceGeometry,
              LinearComponentPatternReferenceInputField.Direction1SelectedReference,
              syncDirection1Definition,
            )"
          >
            <div class="radio-list">
              <n-radio :value="LinearComponentPatternDirectionMode.SpacingAndInstances">
                间距与实例数(S)
              </n-radio>
              <n-radio :value="LinearComponentPatternDirectionMode.UpToReference">
                到参考(U)
              </n-radio>
            </div>
          </n-radio-group>

          <template v-if="direction1.mode === LinearComponentPatternDirectionMode.SpacingAndInstances">
            <div class="number-row">
              <span class="field-label">间距</span>
              <n-input-number
                v-model:value="direction1.spacing"
                size="small"
                :precision="2"
                :step="1"
                :min="0"
                :show-button="true"
                @update:value="handleDirection1SpacingChange"
              >
                <template #suffix>mm</template>
              </n-input-number>
            </div>

            <div class="number-row">
              <span class="field-label">实例数</span>
              <n-input-number
                v-model:value="direction1.instances"
                size="small"
                :precision="0"
                :step="1"
                :min="1"
                :show-button="true"
                @update:value="handleDirection1InstancesChange"
              />
            </div>
          </template>

          <template v-else>
            <LinearComponentPatternUpToReferenceFields
              :state="direction1"
              @focus-reference-geometry="activateReferenceInput(LinearComponentPatternReferenceInputField.Direction1ReferenceGeometry)"
              @focus-selected-reference="activateReferenceInput(LinearComponentPatternReferenceInputField.Direction1SelectedReference)"
              @clear-reference-geometry="clearReferenceInput(LinearComponentPatternReferenceInputField.Direction1ReferenceGeometry)"
              @clear-selected-reference="clearReferenceInput(LinearComponentPatternReferenceInputField.Direction1SelectedReference)"
              :is-reference-geometry-active="activeReferenceInputField === LinearComponentPatternReferenceInputField.Direction1ReferenceGeometry"
              :is-selected-reference-active="activeReferenceInputField === LinearComponentPatternReferenceInputField.Direction1SelectedReference"
              @update-state="applyDirection1Patch($event)"
            />
          </template>

          <!-- 线性零部件阵列-旋转实例 开始 -->
          <n-checkbox
            :checked="direction1.rotateInstances === true"
            :disabled="direction1.mode !== LinearComponentPatternDirectionMode.SpacingAndInstances"
            @update:checked="handleDirection1RotateInstancesChange"
          >
            旋转实例(R)
          </n-checkbox>
          <template
            v-if="
              direction1.rotateInstances === true
              && direction1.mode === LinearComponentPatternDirectionMode.SpacingAndInstances
            "
          >
            <div class="selection-row">
              <span class="field-label">旋转轴</span>
              <LinearComponentPatternReferenceList
                :items="direction1.rotationAxisItem ? [direction1.rotationAxisItem] : []"
                :active="activeReferenceInputField === LinearComponentPatternReferenceInputField.Direction1RotationAxis"
                empty-text="未选中旋转轴"
                :visible-rows="1"
                :show-delete-all-action="false"
                @activate="activateReferenceInput(LinearComponentPatternReferenceInputField.Direction1RotationAxis)"
                @delete-item="removeReferenceItem(LinearComponentPatternReferenceInputField.Direction1RotationAxis, $event.index)"
                @delete-all="clearReferenceInput(LinearComponentPatternReferenceInputField.Direction1RotationAxis)"
              />
            </div>

            <div class="number-row">
              <span class="field-label">角度</span>
              <n-input-number
                :value="direction1.rotationAngle"
                size="small"
                :precision="2"
                :step="1"
                :show-button="true"
                @update:value="handleDirection1RotationAngleChange"
              >
                <template #suffix>度</template>
              </n-input-number>
            </div>

            <n-checkbox
              :checked="direction1.reverseRotation === true"
              @update:checked="handleDirection1ReverseRotationChange"
            >
              反转
            </n-checkbox>

            <n-checkbox
              :checked="direction1.fixedRotationAxis !== false"
              @update:checked="handleDirection1FixedRotationAxisChange"
            >
              固定旋转轴
            </n-checkbox>

            <n-checkbox
              :checked="direction1.alignToSeed === true"
              :disabled="direction1.fixedRotationAxis === false"
              @update:checked="handleDirection1AlignToSeedChange"
            >
              对齐到源
            </n-checkbox>

            <div class="number-row">
              <span class="field-label">参考点</span>
              <n-select
                :value="direction1.rotationReferencePoint"
                size="small"
                :options="rotationReferencePointOptions"
                :disabled="direction1.fixedRotationAxis === false || direction1.alignToSeed !== true"
                @update:value="handleDirection1RotationReferencePointChange"
              />
            </div>
          </template>
          <!-- 线性零部件阵列-旋转实例 结束 -->
        </div>
      </n-collapse-item>

      <n-collapse-item title="方向 2(2)" name="direction2">
        <div class="group-block">
          <div class="selection-row">
            <span class="field-label">阵列方向</span>
            <LinearComponentPatternReferenceList
              :items="direction2.directionItems"
              :active="activeReferenceInputField === LinearComponentPatternReferenceInputField.Direction2"
              empty-text="未选中任何方向"
              :visible-rows="1"
              :show-delete-all-action="false"
              @activate="activateReferenceInput(LinearComponentPatternReferenceInputField.Direction2)"
              @delete-item="removeReferenceItem(LinearComponentPatternReferenceInputField.Direction2, $event.index)"
              @delete-all="clearReferenceInput(LinearComponentPatternReferenceInputField.Direction2)"
            />
          </div>
          <!-- 线性零部件阵列-阵列方向反向 开始 -->
          <div class="button-row">
            <span class="field-label"></span>
            <n-checkbox
              :checked="direction2.reverseDirection === true"
              @update:checked="handleDirection2ReverseDirectionChange"
            >
              反转方向
            </n-checkbox>
          </div>
          <!-- 线性零部件阵列-阵列方向反向 结束 -->

          <n-radio-group
            :value="direction2.mode"
            size="small"
            @update:value="handleDirectionModeChange(
              direction2,
              $event,
              LinearComponentPatternReferenceInputField.Direction2,
              LinearComponentPatternReferenceInputField.Direction2ReferenceGeometry,
              LinearComponentPatternReferenceInputField.Direction2SelectedReference,
              syncDirection2Definition,
            )"
          >
            <div class="radio-list">
              <n-radio :value="LinearComponentPatternDirectionMode.SpacingAndInstances">
                间距与实例数(S)
              </n-radio>
              <n-radio :value="LinearComponentPatternDirectionMode.UpToReference">
                到参考(U)
              </n-radio>
            </div>
          </n-radio-group>

          <template v-if="direction2.mode === LinearComponentPatternDirectionMode.SpacingAndInstances">
            <div class="number-row">
              <span class="field-label">间距</span>
              <n-input-number
                v-model:value="direction2.spacing"
                size="small"
                :precision="2"
                :step="1"
                :min="0"
                :show-button="true"
                @update:value="handleDirection2SpacingChange"
              >
                <template #suffix>mm</template>
              </n-input-number>
            </div>

            <div class="number-row">
              <span class="field-label">实例数</span>
              <n-input-number
                v-model:value="direction2.instances"
                size="small"
                :precision="0"
                :step="1"
                :min="1"
                :show-button="true"
                @update:value="handleDirection2InstancesChange"
              />
            </div>
          </template>

          <template v-else>
            <LinearComponentPatternUpToReferenceFields
              :state="direction2"
              @focus-reference-geometry="activateReferenceInput(LinearComponentPatternReferenceInputField.Direction2ReferenceGeometry)"
              @focus-selected-reference="activateReferenceInput(LinearComponentPatternReferenceInputField.Direction2SelectedReference)"
              @clear-reference-geometry="clearReferenceInput(LinearComponentPatternReferenceInputField.Direction2ReferenceGeometry)"
              @clear-selected-reference="clearReferenceInput(LinearComponentPatternReferenceInputField.Direction2SelectedReference)"
              :is-reference-geometry-active="activeReferenceInputField === LinearComponentPatternReferenceInputField.Direction2ReferenceGeometry"
              :is-selected-reference-active="activeReferenceInputField === LinearComponentPatternReferenceInputField.Direction2SelectedReference"
              @update-state="applyDirection2Patch($event)"
            />
          </template>

          <!-- 线性零部件阵列-只阵列源 开始 -->
          <n-checkbox
            :checked="direction2.patternSeedOnly === true"
            :disabled="direction2.instances <= 1"
            @update:checked="handleDirection2PatternSeedOnlyChange"
          >
            只阵列源(P)
          </n-checkbox>
          <!-- 线性零部件阵列-只阵列源 结束 -->
        </div>
      </n-collapse-item>

      <n-collapse-item title="要阵列的零部件(C)" name="components">
        <div class="group-block">
          <div class="selection-row">
            <span class="field-label">零部件</span>
            <LinearComponentPatternReferenceList
              :items="componentItems"
              :active="isComponentsToPatternInputActive"
              empty-text="未选中任何零部件"
              :visible-rows="3"
              @activate="activateComponentsToPatternInput"
              @delete-item="removeComponentItem($event.index)"
              @delete-all="clearComponentItems"
            />
          </div>
        </div>
      </n-collapse-item>

      <n-collapse-item title="要覆盖的实例(I)" name="instances">
        <div class="group-block">
          <div class="field-title">跳过的实例(S)</div>
          <div class="selection-row">
            <span class="field-label">跳过</span>
            <LinearComponentPatternReferenceList
              :items="skippedInstanceItems"
              :active="isSkippedInstancesInputActive"
              empty-text="未跳过任何实例"
              :visible-rows="3"
              @activate="activateSkippedInstancesInput"
              @delete-item="removeSkippedInstanceItem($event.index)"
              @delete-all="clearSkippedInstanceItems"
            />
          </div>
        </div>
      </n-collapse-item>

    </n-collapse>
  </div>
</template>

<script setup lang="ts">
import { onBeforeUnmount, onMounted, reactive, ref } from 'vue';
import {
  NCollapse,
  NCollapseItem,
  NCheckbox,
  NInputNumber,
  NSelect,
  NRadio,
  NRadioGroup,
} from 'naive-ui';
import {
  convertMToMm,
  convertMmToM,
  LinearComponentPatternDirectionMode,
  LinearComponentPatternReferenceOffsetMode,
  LinearComponentPatternRotationReferencePoint,
  LinearComponentPatternSelectionSlot,
  type LinearComponentPatternDirectionDefinitionState,
  type LinearComponentPatternState,
  type LinearComponentPatternSelectionChangePayload,
} from '@puppycad/cad-core';
import type { PropertyManagerContext } from '@/core';
import LinearComponentPatternReferenceList from './LinearComponentPatternReferenceList.vue';
import LinearComponentPatternUpToReferenceFields from './LinearComponentPatternUpToReferenceFields.vue';
import {
  type LinearComponentPatternDirectionState,
  type LinearComponentPatternReferenceItem,
} from '@/core/property-manager/linear-component-pattern/LinearComponentPatternPropertyTypes';

/** 属性面板 Props。 */
interface LinearComponentPatternPropertySectionProps {
  /** 属性面板上下文。 */
  context: PropertyManagerContext;
}

const props = defineProps<LinearComponentPatternPropertySectionProps>();

/**
 * 线性零部件阵列引用输入字段。
 */
enum LinearComponentPatternReferenceInputField {
  /** 方向 1 阵列方向输入框。 */
  Direction1 = 'direction-1',
  //------ 线性零部件阵列-旋转实例 开始------
  /** 方向 1 旋转实例轴输入框。 */
  Direction1RotationAxis = 'direction-1-rotation-axis',
  //------ 线性零部件阵列-旋转实例 结束------
  /** 方向 1 参考几何输入框。 */
  Direction1ReferenceGeometry = 'direction-1-reference-geometry',
  /** 方向 1 所选参考输入框。 */
  Direction1SelectedReference = 'direction-1-selected-reference',
  /** 方向 2 阵列方向输入框。 */
  Direction2 = 'direction-2',
  /** 方向 2 参考几何输入框。 */
  Direction2ReferenceGeometry = 'direction-2-reference-geometry',
  /** 方向 2 所选参考输入框。 */
  Direction2SelectedReference = 'direction-2-selected-reference',
}

/**
 * 默认展开项。
 *
 * 说明：截图中的方向、零部件与实例分组均默认展开，这里保持同等信息密度。
 */
const expandedNames = ref<string[]>(['direction1', 'direction2', 'components', 'instances']);

/** 方向 1 UI 状态。 */
const direction1 = reactive<LinearComponentPatternDirectionState>({
  directionItems: [],
  mode: LinearComponentPatternDirectionMode.SpacingAndInstances,
  spacing: 50,
  instances: 3,
  referenceGeometryItem: null,
  selectedReferenceItem: null,
  offsetDistance: 0,
  referenceOffsetMode: LinearComponentPatternReferenceOffsetMode.BoundingBoxCenter,
  setSpacing: true,
  setInstances: false,
  //------ 线性零部件阵列-阵列方向反向 开始------
  reverseDirection: false,
  //------ 线性零部件阵列-阵列方向反向 结束------
  //------ 线性零部件阵列-旋转实例 开始------
  rotateInstances: false,
  rotationAxisItem: null,
  rotationAngle: 0,
  reverseRotation: false,
  fixedRotationAxis: true,
  alignToSeed: false,
  rotationReferencePoint: LinearComponentPatternRotationReferencePoint.BoundingBoxCenter,
  //------ 线性零部件阵列-旋转实例 结束------
});

/** 方向 2 UI 状态。 */
const direction2 = reactive<LinearComponentPatternDirectionState>({
  directionItems: [],
  mode: LinearComponentPatternDirectionMode.SpacingAndInstances,
  spacing: 50,
  instances: 1,
  referenceGeometryItem: null,
  selectedReferenceItem: null,
  offsetDistance: 0,
  referenceOffsetMode: LinearComponentPatternReferenceOffsetMode.BoundingBoxCenter,
  setSpacing: true,
  setInstances: false,
  //------ 线性零部件阵列-阵列方向反向 开始------
  reverseDirection: false,
  //------ 线性零部件阵列-阵列方向反向 结束------
  //------ 线性零部件阵列-只阵列源 开始------
  patternSeedOnly: false,
  //------ 线性零部件阵列-只阵列源 结束------
});


/** 要阵列的零部件列表项。 */
const componentItems = ref<LinearComponentPatternReferenceItem[]>([]);

/** 跳过实例选择框显示文本。 */
const skippedInstanceItems = ref<LinearComponentPatternReferenceItem[]>([]);

/** 当前正在接收场景引用选择的输入字段。 */
const activeReferenceInputField = ref<LinearComponentPatternReferenceInputField | null>(null);

/** 当前“要阵列的零部件”输入是否处于激活状态。 */
const isComponentsToPatternInputActive = ref<boolean>(false);

/** 当前“跳过实例”输入是否处于激活状态。 */
const isSkippedInstancesInputActive = ref<boolean>(false);

/** 取消 cad-core 选择监听的函数。 */
let stopSelectionChangeListening: (() => void) | null = null;

//------ 线性零部件阵列-旋转实例 开始------
/**
 * 旋转实例参考点下拉选项。
 */
const rotationReferencePointOptions = [
  {
    label: '边界框中心',
    value: LinearComponentPatternRotationReferencePoint.BoundingBoxCenter,
  },
  {
    label: '零部件原点',
    value: LinearComponentPatternRotationReferencePoint.ComponentOrigin,
  },
];
//------ 线性零部件阵列-旋转实例 结束------

/**
 * 获取线性零部件阵列控制器。
 *
 * @returns 控制器；不存在时返回 null。
 */
function getLinearComponentPatternController() {
  return props.context.renderer?.linearComponentPatternController ?? null;
}

/**
 * 根据 UI 引用输入字段解析对应的控制器选择槽位。
 *
 * 设计说明：
 * - UI 中的每个引用字段都映射到 cad-core 中独立的引用槽位；
 * - 这样控制器回写当前激活槽位时，PropertyManager 能准确恢复到具体字段。
 *
 * @param field 当前聚焦的 UI 字段。
 * @returns 对应的控制器选择槽位。
 */
function resolveSelectionSlotForField(
  field: LinearComponentPatternReferenceInputField,
): LinearComponentPatternSelectionSlot {
  //------ 线性零部件阵列-旋转实例 开始------
  if (field === LinearComponentPatternReferenceInputField.Direction1RotationAxis) {
    return LinearComponentPatternSelectionSlot.Direction1RotationAxis;
  }
  //------ 线性零部件阵列-旋转实例 结束------
  if (
    field === LinearComponentPatternReferenceInputField.Direction1ReferenceGeometry
  ) {
    return LinearComponentPatternSelectionSlot.Direction1ReferenceGeometry;
  }
  if (field === LinearComponentPatternReferenceInputField.Direction1SelectedReference) {
    return LinearComponentPatternSelectionSlot.Direction1SelectedReference;
  }
  if (field === LinearComponentPatternReferenceInputField.Direction2) {
    return LinearComponentPatternSelectionSlot.Direction2Reference;
  }
  if (field === LinearComponentPatternReferenceInputField.Direction2ReferenceGeometry) {
    return LinearComponentPatternSelectionSlot.Direction2ReferenceGeometry;
  }
  if (field === LinearComponentPatternReferenceInputField.Direction2SelectedReference) {
    return LinearComponentPatternSelectionSlot.Direction2SelectedReference;
  }
  return LinearComponentPatternSelectionSlot.Direction1Reference;
}

/**
 * 根据控制器选择槽位解析当前激活的 UI 引用字段。
 *
 * @param slot 控制器槽位。
 * @returns UI 引用字段；非引用槽位时返回 `null`。
 */
function resolveReferenceInputFieldForSlot(
  slot: LinearComponentPatternSelectionSlot,
): LinearComponentPatternReferenceInputField | null {
  if (slot === LinearComponentPatternSelectionSlot.Direction1Reference) {
    return LinearComponentPatternReferenceInputField.Direction1;
  }
  //------ 线性零部件阵列-旋转实例 开始------
  if (slot === LinearComponentPatternSelectionSlot.Direction1RotationAxis) {
    return LinearComponentPatternReferenceInputField.Direction1RotationAxis;
  }
  //------ 线性零部件阵列-旋转实例 结束------
  if (slot === LinearComponentPatternSelectionSlot.Direction1ReferenceGeometry) {
    return LinearComponentPatternReferenceInputField.Direction1ReferenceGeometry;
  }
  if (slot === LinearComponentPatternSelectionSlot.Direction1SelectedReference) {
    return LinearComponentPatternReferenceInputField.Direction1SelectedReference;
  }
  if (slot === LinearComponentPatternSelectionSlot.Direction2Reference) {
    return LinearComponentPatternReferenceInputField.Direction2;
  }
  if (slot === LinearComponentPatternSelectionSlot.Direction2ReferenceGeometry) {
    return LinearComponentPatternReferenceInputField.Direction2ReferenceGeometry;
  }
  if (slot === LinearComponentPatternSelectionSlot.Direction2SelectedReference) {
    return LinearComponentPatternReferenceInputField.Direction2SelectedReference;
  }
  return null;
}

/**
 * 激活引用输入槽位。
 *
 * @param field 当前聚焦的 UI 字段。
 */
function activateReferenceInput(field: LinearComponentPatternReferenceInputField): void {
  //------ 线性零部件阵列-自动跳转输入目标 开始------
  isComponentsToPatternInputActive.value = false;
  isSkippedInstancesInputActive.value = false;
  //------ 线性零部件阵列-自动跳转输入目标 结束------
  activeReferenceInputField.value = field;
  // 根据具体 UI 字段映射到 cad-core 独立槽位，
  // 确保切换到“到参考”时能精确聚焦到“参考几何”而不是回退到“阵列方向”。
  getLinearComponentPatternController()?.setActiveSelectionSlot?.(resolveSelectionSlotForField(field));
}

/**
 * 处理方向定义方式切换。
 *
 * @param target 目标方向状态。
 * @param value 新模式值。
 * @param directionField 常规方向字段。
 * @param referenceGeometryField 参考几何字段。
 * @param selectedReferenceField 所选参考字段。
 */
function handleDirectionModeChange(
  target: LinearComponentPatternDirectionState,
  value: string | number | boolean,
  directionField: LinearComponentPatternReferenceInputField,
  referenceGeometryField: LinearComponentPatternReferenceInputField,
  selectedReferenceField: LinearComponentPatternReferenceInputField,
  syncDefinition: () => void,
): void {
  if (
    value !== LinearComponentPatternDirectionMode.SpacingAndInstances
    && value !== LinearComponentPatternDirectionMode.UpToReference
  ) {
    return;
  }

  target.mode = value;
  syncDefinition();

  if (value === LinearComponentPatternDirectionMode.UpToReference) {
    //------ 线性零部件阵列-到参考参数切换 开始------
    if (target.setSpacing === target.setInstances) {
      target.setSpacing = true;
      target.setInstances = false;
    }
    //------ 线性零部件阵列-到参考参数切换 结束------
    syncDefinition();
    activateReferenceInput(referenceGeometryField);
    return;
  }

  if (
    activeReferenceInputField.value === referenceGeometryField
    || activeReferenceInputField.value === selectedReferenceField
  ) {
    activateReferenceInput(directionField);
  }
}

/**
 * 激活“要阵列的零部件”输入槽位。
 */
function activateComponentsToPatternInput(): void {
  //------ 线性零部件阵列-自动跳转输入目标 开始------
  activeReferenceInputField.value = null;
  isComponentsToPatternInputActive.value = true;
  isSkippedInstancesInputActive.value = false;
  getLinearComponentPatternController()?.setActiveSelectionSlot?.(
    LinearComponentPatternSelectionSlot.ComponentsToPattern,
  );
  //------ 线性零部件阵列-自动跳转输入目标 结束------
}

/**
 * 激活“跳过实例”输入槽位。
 */
function activateSkippedInstancesInput(): void {
  //------ 线性零部件阵列-跳过实例预览 开始------
  activeReferenceInputField.value = null;
  isComponentsToPatternInputActive.value = false;
  isSkippedInstancesInputActive.value = true;
  getLinearComponentPatternController()?.setActiveSelectionSlot?.(
    LinearComponentPatternSelectionSlot.SkippedInstances,
  );
  //------ 线性零部件阵列-跳过实例预览 结束------
}

/**
 * 根据控制器状态同步“要阵列的零部件”显示文本。
 *
 * @param componentObjectIds 已选零部件对象 ID 列表。
 */
function syncComponentItems(componentObjectIds: readonly string[]): void {
  //------ 线性零部件阵列-零部件选择状态同步 开始------
  componentItems.value = componentObjectIds.map((objectId) => ({
    objectId,
    label: resolveComponentItemLabel(objectId),
  }));
  //------ 线性零部件阵列-零部件选择状态同步 结束------
}

/**
 * 解析零部件列表项显示名。
 *
 * @param objectId 零部件对象 ID。
 * @returns 可展示名称。
 */
function resolveComponentItemLabel(objectId: string): string {
  return getLinearComponentPatternController()
    ?.resolveComponentDisplayNameByObjectId?.(objectId)
    ?? objectId;
}

/**
 * 根据控制器状态同步“跳过实例”显示列表。
 *
 * @param skippedInstanceKeys 已跳过逻辑实例键列表。
 */
function syncSkippedInstanceItems(skippedInstanceKeys: readonly string[]): void {
  //------ 线性零部件阵列-跳过实例预览 开始------
  skippedInstanceItems.value = skippedInstanceKeys.map((instanceKey) => ({
    objectId: instanceKey,
    label: resolveSkippedInstanceLabel(instanceKey),
  }));
  //------ 线性零部件阵列-跳过实例预览 结束------
}

/**
 * 将逻辑实例键格式化为 UI 显示名称。
 *
 * 说明：
 * 1. 内部逻辑实例键从 0 开始计数；
 * 2. PropertyManager 需要按用户感知的 1 基序号展示；
 * 3. 源实例占据 `(1,1)`，因此第一个阵列实例显示为 `(2,1)`。
 *
 * @param instanceKey 逻辑实例键。
 * @returns UI 显示名称。
 */
function resolveSkippedInstanceLabel(instanceKey: string): string {
  const [direction1IndexText, direction2IndexText] = instanceKey.split(':');
  const direction1Index = Number(direction1IndexText);
  const direction2Index = Number(direction2IndexText);
  if (!Number.isFinite(direction1Index) || !Number.isFinite(direction2Index)) {
    return instanceKey;
  }
  return `(${direction1Index + 1}, ${direction2Index + 1})`;
}

/**
 * 删除单个跳过实例项。
 *
 * @param index 列表索引。
 */
function removeSkippedInstanceItem(index: number): void {
  const targetItem = skippedInstanceItems.value[index];
  if (!targetItem) {
    return;
  }
  getLinearComponentPatternController()?.removeSkippedInstanceKey?.(targetItem.objectId);
}

/**
 * 清空全部跳过实例项。
 */
function clearSkippedInstanceItems(): void {
  getLinearComponentPatternController()?.clearSkippedInstanceKeys?.();
}

//------ 线性零部件阵列-零部件列表移除 开始------
/**
 * 删除单个要阵列的零部件项。
 *
 * @param index 列表索引。
 */
function removeComponentItem(index: number): void {
  const targetItem = componentItems.value[index];
  if (!targetItem) {
    return;
  }
  getLinearComponentPatternController()?.removeComponentObjectId?.(targetItem.objectId);
}

/**
 * 清空全部要阵列的零部件项。
 */
function clearComponentItems(): void {
  getLinearComponentPatternController()?.clearComponentObjectIds?.();
}
//------ 线性零部件阵列-零部件列表移除 结束------

/**
 * 根据控制器当前槽位同步面板激活态。
 *
 * @param slot 当前激活槽位。
 */
function syncActiveInputBySlot(slot: LinearComponentPatternSelectionSlot): void {
  //------ 线性零部件阵列-零部件选择状态同步 开始------
  const referenceField = resolveReferenceInputFieldForSlot(slot);
  if (referenceField) {
    activeReferenceInputField.value = referenceField;
    isComponentsToPatternInputActive.value = false;
    isSkippedInstancesInputActive.value = false;
    return;
  }
  if (slot === LinearComponentPatternSelectionSlot.ComponentsToPattern) {
    activeReferenceInputField.value = null;
    isComponentsToPatternInputActive.value = true;
    isSkippedInstancesInputActive.value = false;
    return;
  }
  if (slot === LinearComponentPatternSelectionSlot.SkippedInstances) {
    activeReferenceInputField.value = null;
    isComponentsToPatternInputActive.value = false;
    isSkippedInstancesInputActive.value = true;
    return;
  }
  isComponentsToPatternInputActive.value = false;
  isSkippedInstancesInputActive.value = false;
  //------ 线性零部件阵列-零部件选择状态同步 结束------
}

/**
 * 由控制器状态同步所有引用类字段显示值。
 *
 * @param state 控制器状态快照。
 */
function syncReferenceInputsFromState(state: LinearComponentPatternState): void {
  setReferenceInputValue(
    LinearComponentPatternReferenceInputField.Direction1,
    createReferenceItem(state.direction1ReferenceObjectId, state.direction1ReferenceDisplayName),
  );
  //------ 线性零部件阵列-旋转实例 开始------
  setReferenceInputValue(
    LinearComponentPatternReferenceInputField.Direction1RotationAxis,
    createReferenceItem(state.direction1RotationAxisObjectId, state.direction1RotationAxisDisplayName),
  );
  //------ 线性零部件阵列-旋转实例 结束------
  setReferenceInputValue(
    LinearComponentPatternReferenceInputField.Direction1ReferenceGeometry,
    createReferenceItem(state.direction1ReferenceGeometryObjectId, state.direction1ReferenceGeometryDisplayName),
  );
  setReferenceInputValue(
    LinearComponentPatternReferenceInputField.Direction1SelectedReference,
    createReferenceItem(state.direction1SelectedReferenceObjectId, state.direction1SelectedReferenceDisplayName),
  );
  setReferenceInputValue(
    LinearComponentPatternReferenceInputField.Direction2,
    createReferenceItem(state.direction2ReferenceObjectId, state.direction2ReferenceDisplayName),
  );
  setReferenceInputValue(
    LinearComponentPatternReferenceInputField.Direction2ReferenceGeometry,
    createReferenceItem(state.direction2ReferenceGeometryObjectId, state.direction2ReferenceGeometryDisplayName),
  );
  setReferenceInputValue(
    LinearComponentPatternReferenceInputField.Direction2SelectedReference,
    createReferenceItem(state.direction2SelectedReferenceObjectId, state.direction2SelectedReferenceDisplayName),
  );
}

/**
 * 由控制器状态同步方向定义字段。
 *
 * @param state 控制器状态快照。
 */
function syncDirectionDefinitionsFromState(state: LinearComponentPatternState): void {
  direction1.mode = state.direction1Mode;
  direction1.spacing = convertMToMm(state.direction1Spacing);
  direction1.instances = state.direction1Instances;
  direction1.offsetDistance = convertMToMm(state.direction1OffsetDistance);
  direction1.referenceOffsetMode = state.direction1ReferenceOffsetMode;
  direction1.setSpacing = state.direction1SetSpacing;
  direction1.setInstances = state.direction1SetInstances;
  //------ 线性零部件阵列-阵列方向反向 开始------
  direction1.reverseDirection = state.direction1ReverseDirection;
  //------ 线性零部件阵列-阵列方向反向 结束------
  //------ 线性零部件阵列-旋转实例 开始------
  direction1.rotateInstances = state.direction1RotateInstances;
  direction1.rotationAngle = state.direction1RotationAngle;
  direction1.reverseRotation = state.direction1ReverseRotation;
  direction1.fixedRotationAxis = state.direction1FixedRotationAxis;
  direction1.alignToSeed = state.direction1AlignToSeed;
  direction1.rotationReferencePoint = state.direction1RotationReferencePoint;
  //------ 线性零部件阵列-旋转实例 结束------

  direction2.mode = state.direction2Mode;
  direction2.spacing = convertMToMm(state.direction2Spacing);
  direction2.instances = state.direction2Instances;
  direction2.offsetDistance = convertMToMm(state.direction2OffsetDistance);
  direction2.referenceOffsetMode = state.direction2ReferenceOffsetMode;
  direction2.setSpacing = state.direction2SetSpacing;
  direction2.setInstances = state.direction2SetInstances;
  //------ 线性零部件阵列-阵列方向反向 开始------
  direction2.reverseDirection = state.direction2ReverseDirection;
  //------ 线性零部件阵列-阵列方向反向 结束------
  //------ 线性零部件阵列-只阵列源 开始------
  direction2.patternSeedOnly = state.direction2PatternSeedOnly;
  //------ 线性零部件阵列-只阵列源 结束------
}

/**
 * 从控制器同步当前状态到 PropertyManager。
 */
function syncFromController(): void {
  //------ 线性零部件阵列-零部件选择状态同步 开始------
  const state = getLinearComponentPatternController()?.getViewModel?.().state;
  if (!state) {
    return;
  }
  syncDirectionDefinitionsFromState(state);
  syncReferenceInputsFromState(state);
  syncComponentItems(state.componentObjectIds);
  syncSkippedInstanceItems(state.skippedInstanceKeys);
  syncActiveInputBySlot(state.activeSelectionSlot);
  //------ 线性零部件阵列-零部件选择状态同步 结束------
}

/**
 * 按 SOLIDWORKS 常用录入顺序解析下一个应激活的输入字段。
 *
 * @param field 当前已完成选择的字段。
 * @returns 下一个字段；无下一个时返回 null。
 */
function resolveNextReferenceInputField(
  field: LinearComponentPatternReferenceInputField,
): LinearComponentPatternReferenceInputField | null {
  //------ 线性零部件阵列-自动跳转输入目标 开始------
  if (field === LinearComponentPatternReferenceInputField.Direction1) {
    return LinearComponentPatternReferenceInputField.Direction2;
  }
  return null;
  //------ 线性零部件阵列-自动跳转输入目标 结束------
}

/**
 * 清除引用输入字段。
 *
 * @param field 待清除字段。
 */
function clearReferenceInput(field: LinearComponentPatternReferenceInputField): void {
  setReferenceInputValue(field, null);
  activateReferenceInput(field);
  getLinearComponentPatternController()?.clearReferenceInputSelection?.();
}

/**
 * 删除引用列表中的指定项。
 *
 * @param field 目标字段。
 * @param index 待删除索引。
 */
function removeReferenceItem(field: LinearComponentPatternReferenceInputField, index: number): void {
  if (field === LinearComponentPatternReferenceInputField.Direction1) {
    direction1.directionItems.splice(index, 1);
  }
  //------ 线性零部件阵列-旋转实例 开始------
  if (field === LinearComponentPatternReferenceInputField.Direction1RotationAxis) {
    direction1.rotationAxisItem = null;
  }
  //------ 线性零部件阵列-旋转实例 结束------
  if (field === LinearComponentPatternReferenceInputField.Direction1ReferenceGeometry) {
    direction1.referenceGeometryItem = null;
  }
  if (field === LinearComponentPatternReferenceInputField.Direction1SelectedReference) {
    direction1.selectedReferenceItem = null;
  }
  if (field === LinearComponentPatternReferenceInputField.Direction2) {
    direction2.directionItems.splice(index, 1);
  }
  if (field === LinearComponentPatternReferenceInputField.Direction2ReferenceGeometry) {
    direction2.referenceGeometryItem = null;
  }
  if (field === LinearComponentPatternReferenceInputField.Direction2SelectedReference) {
    direction2.selectedReferenceItem = null;
  }
  activateReferenceInput(field);
  getLinearComponentPatternController()?.clearReferenceInputSelection?.();
}

/**
 * 写入引用输入字段显示值。
 *
 * @param field 目标字段。
 * @param item 引用项；传入 null 时清空。
 */
function setReferenceInputValue(
  field: LinearComponentPatternReferenceInputField,
  item: LinearComponentPatternReferenceItem | null,
): void {
  if (field === LinearComponentPatternReferenceInputField.Direction1) {
    replaceReferenceItems(direction1.directionItems, item);
    return;
  }
  //------ 线性零部件阵列-旋转实例 开始------
  if (field === LinearComponentPatternReferenceInputField.Direction1RotationAxis) {
    direction1.rotationAxisItem = item;
    return;
  }
  //------ 线性零部件阵列-旋转实例 结束------
  if (field === LinearComponentPatternReferenceInputField.Direction1ReferenceGeometry) {
    direction1.referenceGeometryItem = item;
    return;
  }
  if (field === LinearComponentPatternReferenceInputField.Direction1SelectedReference) {
    direction1.selectedReferenceItem = item;
    return;
  }
  if (field === LinearComponentPatternReferenceInputField.Direction2) {
    replaceReferenceItems(direction2.directionItems, item);
    return;
  }
  if (field === LinearComponentPatternReferenceInputField.Direction2ReferenceGeometry) {
    direction2.referenceGeometryItem = item;
    return;
  }
  if (field === LinearComponentPatternReferenceInputField.Direction2SelectedReference) {
    direction2.selectedReferenceItem = item;
  }
}

/**
 * 根据对象 ID 与显示名构造单选引用项。
 *
 * @param objectId 对象 ID。
 * @param label 显示名称。
 * @returns 引用项；无效时返回 `null`。
 */
function createReferenceItem(
  objectId: string | null,
  label: string,
): LinearComponentPatternReferenceItem | null {
  if (!objectId) {
    return null;
  }
  return {
    objectId,
    label,
  };
}

/**
 * 替换单选引用列表。
 *
 * @param items 目标列表。
 * @param item 新引用项；为空时清空。
 */
function replaceReferenceItems(
  items: LinearComponentPatternReferenceItem[],
  item: LinearComponentPatternReferenceItem | null,
): void {
  items.splice(0, items.length);
  if (item) {
    items.push(item);
  }
}

/**
 * 处理 cad-core 场景选择回写。
 *
 * 设计说明：
 * - 引用槽位：直接更新对应选择列表，并在需要时按 SolidWorks 录入顺序自动跳槽；
 * - ComponentsToPattern 槽位：直接同步 item 列表，避免依赖 120ms 轮询产生感知延迟；
 * - SkippedInstances 槽位：直接同步已跳过实例列表，列表名称使用逻辑实例序号；
 *
 * @param payload 选择变化载荷。
 */
function handlePatternSelectionChange(payload: LinearComponentPatternSelectionChangePayload): void {
  //------ 线性零部件阵列-组件槽事件驱动同步 开始------
  if (payload.activeSelectionSlot === LinearComponentPatternSelectionSlot.ComponentsToPattern) {
    // 组件槽：payload 携带完整 componentObjectIds，直接同步 item 列表，无需等待轮询。
    syncComponentItems(payload.componentObjectIds ?? []);
    return;
  }
  //------ 线性零部件阵列-组件槽事件驱动同步 结束------

  //------ 线性零部件阵列-跳过实例预览 开始------
  if (payload.activeSelectionSlot === LinearComponentPatternSelectionSlot.SkippedInstances) {
    syncSkippedInstanceItems(payload.skippedInstanceKeys ?? []);
    return;
  }
  //------ 线性零部件阵列-跳过实例预览 结束------

  const field = resolveReferenceInputFieldForSlot(payload.activeSelectionSlot);
  if (!field) {
    return;
  }
  activeReferenceInputField.value = field;
  if (!payload.objectId) {
    setReferenceInputValue(field, null);
    return;
  }
  setReferenceInputValue(field, {
    objectId: payload.objectId,
    label: payload.displayName,
  });

  //------ 线性零部件阵列-自动跳转输入目标 开始------
  const nextField = resolveNextReferenceInputField(field);
  if (nextField) {
    activateReferenceInput(nextField);
    return;
  }
  if (field === LinearComponentPatternReferenceInputField.Direction2) {
    activateComponentsToPatternInput();
  }
  //------ 线性零部件阵列-自动跳转输入目标 结束------
}

onMounted(() => {
  stopSelectionChangeListening = getLinearComponentPatternController()?.onSelectionChange?.(
    handlePatternSelectionChange,
  ) ?? null;

  //------ 线性零部件阵列-零部件选择状态同步 开始------
  // 挂载时同步一次，确保重新打开 PropertyManager 时标签即时刷新。
  // 三个槽的后续变更均通过 onSelectionChange 事件驱动，不再依赖轮询。
  syncFromController();
  //------ 线性零部件阵列-零部件选择状态同步 结束------
});

onBeforeUnmount(() => {
  stopSelectionChangeListening?.();
  stopSelectionChangeListening = null;
});

/**
 * 处理折叠区展开状态变更。
 *
 * @param names NaiveUI 返回的展开项名称。
 */
function handleExpandedNamesChange(names: Array<string | number>): void {
  expandedNames.value = names.map((name) => String(name));
}

/**
 * 构造 controller 需要的方向定义状态。
 *
 * @param target 目标方向状态。
 * @returns 可写入 controller 的方向定义状态。
 */
function createDirectionDefinitionState(
  target: LinearComponentPatternDirectionState,
): LinearComponentPatternDirectionDefinitionState {
  return {
    mode: target.mode,
    spacing: convertMmToM(target.spacing),
    instances: target.instances,
    offsetDistance: convertMmToM(target.offsetDistance),
    referenceOffsetMode: target.referenceOffsetMode,
    setSpacing: target.setSpacing,
    setInstances: target.setInstances,
    //------ 线性零部件阵列-阵列方向反向 开始------
    reverseDirection: target.reverseDirection === true,
    //------ 线性零部件阵列-阵列方向反向 结束------
    //------ 线性零部件阵列-只阵列源 开始------
    patternSeedOnly: target.patternSeedOnly === true,
    //------ 线性零部件阵列-只阵列源 结束------
    //------ 线性零部件阵列-旋转实例 开始------
    rotateInstances: target.rotateInstances === true,
    rotationAngle: target.rotationAngle ?? 0,
    reverseRotation: target.reverseRotation === true,
    fixedRotationAxis: target.fixedRotationAxis !== false,
    alignToSeed: target.fixedRotationAxis !== false && target.alignToSeed === true,
    rotationReferencePoint: target.rotationReferencePoint ?? LinearComponentPatternRotationReferencePoint.BoundingBoxCenter,
    //------ 线性零部件阵列-旋转实例 结束------
  };
}

/**
 * 同步方向 1 定义到控制器。
 */
function syncDirection1Definition(): void {
  getLinearComponentPatternController()?.updateDirection1Definition?.(
    createDirectionDefinitionState(direction1),
  );
}

/**
 * 同步方向 2 定义到控制器。
 */
function syncDirection2Definition(): void {
  getLinearComponentPatternController()?.updateDirection2Definition?.(
    createDirectionDefinitionState(direction2),
  );
}

//------ 线性零部件阵列-阵列方向反向 开始------
/**
 * 处理方向 1 阵列传播方向反向勾选变更。
 *
 * @param checked 是否反转方向 1。
 */
function handleDirection1ReverseDirectionChange(checked: boolean): void {
  direction1.reverseDirection = checked;
  syncDirection1Definition();
}

/**
 * 处理方向 2 阵列传播方向反向勾选变更。
 *
 * @param checked 是否反转方向 2。
 */
function handleDirection2ReverseDirectionChange(checked: boolean): void {
  direction2.reverseDirection = checked;
  syncDirection2Definition();
}
//------ 线性零部件阵列-阵列方向反向 结束------

/**
 * 应用方向 1 子组件状态补丁。
 *
 * @param patch 子组件上抛的状态补丁。
 */
function applyDirection1Patch(patch: Partial<LinearComponentPatternDirectionState>): void {
  Object.assign(direction1, patch);
  syncDirection1Definition();
}

/**
 * 应用方向 2 子组件状态补丁。
 *
 * @param patch 子组件上抛的状态补丁。
 */
function applyDirection2Patch(patch: Partial<LinearComponentPatternDirectionState>): void {
  Object.assign(direction2, patch);
  syncDirection2Definition();
}

/**
 * 处理方向 1 间距变更，并立即通知控制器触发预览计算。
 *
 * @param spacing 方向 1 间距。
 */
function handleDirection1SpacingChange(spacing: number | null): void {
  direction1.spacing = spacing ?? 0;
  syncDirection1Definition();
}

/**
 * 处理方向 1 实例数变更，并立即通知控制器触发预览计算。
 *
 * @param instances 方向 1 实例数。
 */
function handleDirection1InstancesChange(instances: number | null): void {
  direction1.instances = instances ?? 1;
  syncDirection1Definition();
}

//------ 线性零部件阵列-旋转实例 开始------
/**
 * 同步方向 1 旋转实例参数到控制器。
 */
function syncDirection1RotationParams(): void {
  getLinearComponentPatternController()?.updateDirection1RotationParams?.(
    direction1.rotateInstances === true,
    direction1.rotationAngle ?? 0,
    direction1.reverseRotation === true,
    direction1.fixedRotationAxis !== false,
    direction1.fixedRotationAxis !== false && direction1.alignToSeed === true,
    direction1.rotationReferencePoint ?? LinearComponentPatternRotationReferencePoint.BoundingBoxCenter,
  );
}

/**
 * 处理方向 1 旋转实例开关变更。
 *
 * @param checked 是否启用旋转实例。
 */
function handleDirection1RotateInstancesChange(checked: boolean): void {
  direction1.rotateInstances = checked;
  syncDirection1RotationParams();
}

/**
 * 处理方向 1 旋转角度变更。
 *
 * @param angle 角度增量（角度制）。
 */
function handleDirection1RotationAngleChange(angle: number | null): void {
  direction1.rotationAngle = angle ?? 0;
  syncDirection1RotationParams();
}

/**
 * 处理方向 1 旋转反向开关变更。
 *
 * @param checked 是否反转旋转方向。
 */
function handleDirection1ReverseRotationChange(checked: boolean): void {
  direction1.reverseRotation = checked;
  syncDirection1RotationParams();
}

/**
 * 处理方向 1 固定旋转轴开关变更。
 *
 * @param checked 是否固定旋转轴。
 */
function handleDirection1FixedRotationAxisChange(checked: boolean): void {
  direction1.fixedRotationAxis = checked;
  if (!checked) {
    direction1.alignToSeed = false;
  }
  syncDirection1RotationParams();
}

/**
 * 处理方向 1 对齐到源开关变更。
 *
 * @param checked 是否对齐到源。
 */
function handleDirection1AlignToSeedChange(checked: boolean): void {
  direction1.alignToSeed = direction1.fixedRotationAxis !== false && checked;
  syncDirection1RotationParams();
}

/**
 * 处理方向 1 旋转实例参考点变更。
 *
 * @param value 参考点枚举值。
 */
function handleDirection1RotationReferencePointChange(value: string): void {
  direction1.rotationReferencePoint = value as LinearComponentPatternRotationReferencePoint;
  syncDirection1RotationParams();
}
//------ 线性零部件阵列-旋转实例 结束------

/**
 * 处理方向 2 间距变更，并立即通知控制器触发预览计算。
 *
 * @param spacing 方向 2 间距。
 */
function handleDirection2SpacingChange(spacing: number | null): void {
  direction2.spacing = spacing ?? 0;
  syncDirection2Definition();
}

/**
 * 处理方向 2 实例数变更，并立即通知控制器触发预览计算。
 *
 * @param instances 方向 2 实例数。
 */
function handleDirection2InstancesChange(instances: number | null): void {
  direction2.instances = instances ?? 1;
  syncDirection2Definition();
}

//------ 线性零部件阵列-只阵列源 开始------
/**
 * 处理方向 2 只阵列源开关变更。
 *
 * @param checked 是否只复制源零部件。
 */
function handleDirection2PatternSeedOnlyChange(checked: boolean): void {
  direction2.patternSeedOnly = checked;
  syncDirection2Definition();
}
//------ 线性零部件阵列-只阵列源 结束------
</script>

<style scoped lang="less">
.linear-component-pattern-property {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.pm-collapse :deep(.n-collapse-item__header) {
  padding: 6px 0;
}

.pm-collapse :deep(.n-collapse-item__content-inner) {
  padding-top: 4px;
}

.group-block {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.selection-row,
.button-row,
.number-row {
  display: grid;
  grid-template-columns: 52px minmax(0, 1fr);
  align-items: center;
  gap: 6px;
}

.field-label {
  font-size: 12px;
  color: var(--n-text-color);
  white-space: nowrap;
  text-align: right;
}

.selection-input {
  min-width: 0;
}

.selection-input--active {
  border-radius: 3px;
}

.selection-input--active :deep(.n-input-wrapper) {
  box-shadow: 0 0 0 1px #2f8cff inset;
  background: rgba(47, 140, 255, 0.08);
}

.radio-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding-left: 58px;
}

.field-title {
  font-size: 12px;
  font-weight: 600;
  color: var(--n-text-color);
}

</style>
